import React, { useEffect, useMemo, useState } from 'react';

import http from '../../../api/http';
import RecordSection from '../../view/RecordSection';

const WIDTH = 720;
const HEIGHT = 180;
const PADDING = 24;

const MetricTrendSection = ({ sprintPackId, metricKey, metricLabel, unit }) => {
  const [points, setPoints] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    http.get(`/metrics/series/${encodeURIComponent(sprintPackId)}/${encodeURIComponent(metricKey)}`)
      .then(({ data }) => {
        if (!active) return;
        if (!Array.isArray(data?.data)) throw new Error('Metric history response is invalid.');
        setPoints(data.data);
        setTruncated(Boolean(data.truncated));
      })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.message || requestError.message || 'Unable to load metric history.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [sprintPackId, metricKey]);

  const chart = useMemo(() => {
    const numericValues = points.map((point) => Number(point.value));
    if (!points.length || numericValues.some((value) => !Number.isFinite(value))) return null;
    const min = Math.min(...numericValues);
    const max = Math.max(...numericValues);
    const range = max - min || 1;
    const xStep = points.length > 1 ? (WIDTH - 2 * PADDING) / (points.length - 1) : 0;
    const coordinates = points.map((point, index) => ({
      point,
      x: points.length > 1 ? PADDING + index * xStep : WIDTH / 2,
      y: HEIGHT - PADDING - ((Number(point.value) - min) / range) * (HEIGHT - 2 * PADDING),
    }));
    return { coordinates, polyline: coordinates.map(({ x, y }) => `${x},${y}`).join(' ') };
  }, [points]);

  return (
    <RecordSection title={`${metricLabel || metricKey} history`} className="record-metric-trend">
      {loading ? <p>Loading metric history...</p> : null}
      {error ? <p className="error-text" role="alert">{error}</p> : null}
      {!loading && !error && !points.length ? <p>No observations recorded for this metric yet.</p> : null}
      {!loading && !error && chart ? (
        <>
          <div className="card">
            <svg
              aria-label={`${metricLabel || metricKey} trend chart`}
              role="img"
              viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              style={{ display: 'block', width: '100%', maxHeight: HEIGHT }}
            >
              <polyline
                fill="none"
                points={chart.polyline}
                stroke="currentColor"
                strokeWidth="3"
                vectorEffect="non-scaling-stroke"
              />
              {chart.coordinates.map(({ point, x, y }) => {
                const hasAnomaly = point.anomalies?.length > 0;
                return (
                  <circle
                    key={point.id}
                    cx={x}
                    cy={y}
                    fill={hasAnomaly ? '#c2413b' : 'currentColor'}
                    r={hasAnomaly ? 6 : 4}
                  >
                    <title>
                      {`${point.recordedOn}: ${point.value}${unit ? ` ${unit}` : ''}${hasAnomaly ? ` (${point.anomalies.map((item) => item.type).join(', ')})` : ''}`}
                    </title>
                  </circle>
                );
              })}
            </svg>
            <div className="row-between small-muted">
              <span>{points[0].recordedOn}</span>
              <span>{points[points.length - 1].recordedOn}</span>
            </div>
          </div>
          {truncated ? <p className="small-muted">Showing the latest 120 observations.</p> : null}
          <ul className="record-health-signals">
            {chart.coordinates.flatMap(({ point }) => (point.anomalies || []).map((anomaly, index) => (
              <li key={`${point.id}:${index}`}>
                <strong>{anomaly.type.replace(/_/g, ' ')}</strong>
                <span>{point.recordedOn}: {point.value}{unit ? ` ${unit}` : ''}</span>
              </li>
            )))}
          </ul>
        </>
      ) : null}
    </RecordSection>
  );
};

export default MetricTrendSection;
